import React, { useState, useEffect } from "react";
import {
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Input,
  Row,
  Col,
} from "reactstrap";
import { FormattedMessage } from "react-intl";
import { GetCustomerGroups } from "../../actions/superUserQueries";
import { useMutation } from "@tanstack/react-query";
import GroupCard from "../Groups/GroupCard";
import {
  EditSchoolGroupName,
  CreateSchoolGroup,
  EditSchoolGrouping,
  DeleteSchoolGroup,
} from "../../actions/superUserMutations";
import { toast } from "react-toastify";
import { useQueryClient } from "@tanstack/react-query";
import ConfirmDialog from "./ConfirmDialog";

export default function EditGroupsModal({ close, isOpen, schools }) {
  const [addNewGroupOpen, setAddNewGroupOpen] = useState(false);
  const [newGroupName, setNewGroupName] = useState("");
  const [newChanges, setNewChanges] = useState([]);
  const [currentSchools, setCurrentSchools] = useState(
    structuredClone(schools)
  );
  const [changedNames, setChangedNames] = useState([]);
  const [promptForDelete, setPromptForDelete] = useState(null);
  const [updateMade, setUpdateMade] = useState(false);

  const groups = GetCustomerGroups();

  const handleEditSchoolGroupName = useMutation({ mutationFn: EditSchoolGroupName });
  const handleCreateSchoolGroup = useMutation({ mutationFn: CreateSchoolGroup });
  const handleEditSchoolGrouping = useMutation({ mutationFn: EditSchoolGrouping });
  const handleDeleteSchoolGroup = useMutation({ mutationFn: DeleteSchoolGroup });
  const queryClient = useQueryClient();

  const onProceed = () => {
    handleChangeNames();
    if (newChanges.length > 0) {
      handleChangeGroups();
    }
  };

  useEffect(() => {
    if (updateMade) {
      queryClient.invalidateQueries({ queryKey: ["FetchSchoolsForAdmin"] });
      close();
      setUpdateMade(false);
    }
  }, [updateMade, close, queryClient]);

  const handleDeleteGroup = () => {
    handleDeleteSchoolGroup.mutate(
      {
        groupId: promptForDelete.id,
      },
      {
        onSuccess: () => {
          groups.refetch();
          toast.success(<FormattedMessage id="GroupDeleted" />);
          setPromptForDelete(null);
        },
        onError: (data) => {
          toast.warn(data.response.data);
        },
      }
    );
  };

  const handleChangeGroups = () => {
    const schoolsToPost = [];
    newChanges.forEach((groupCoupling) => {
      schoolsToPost.push({
        CustomerId: groupCoupling.schoolId,
        GroupId: groupCoupling.group,
      });
    });
    handleEditSchoolGrouping.mutate(
      {
        customers: schoolsToPost,
      },
      {
        onSuccess: () => {
          toast.success(<FormattedMessage id="GroupsUpdated" />);
          setUpdateMade(true);
          setNewChanges([]);
        },
        onError: (data) => {
          toast.warn(data.response.data);
        },
      }
    );
  };

  const handleChangeNames = () => {
    const namesToPost = [];
    changedNames.forEach((item) => {
      if (
        groups.data.find((group) => group.id === item.id).name !== item.name
      ) {
        namesToPost.push(item);
      }
    });
    namesToPost.forEach((change) => {
      handleEditSchoolGroupName.mutate(
        {
          id: change.id,
          name: change.name,
        },
        {
          onSuccess: () => {
            setUpdateMade(true);
            toast.success(<FormattedMessage id="NameChanged" />);
          },
          onError: (data) => {
            toast.warn(data.response.data);
          },
        }
      );
    });
  };

  const handlePostNewGroup = () => {
    handleCreateSchoolGroup.mutate(
      {
        name: newGroupName,
      },
      {
        onSuccess: () => {
          groups.refetch();
          toast.success(<FormattedMessage id="GroupAdded" />);
        },
        onError: (data) => {
          toast.warn(data.response.data);
        },
      }
    );
    setNewGroupName("");
    setAddNewGroupOpen(false);
  };

  const handleCloseModal = () => {
    setCurrentSchools(structuredClone(schools));
    close();
  };

  const addNewGroup = () => {
    if (addNewGroupOpen) {
      return (
        <>
          <Col lg={6}>
            <Input
              type="text"
              onChange={(data) => setNewGroupName(data.target.value)}
            ></Input>
          </Col>
          <Col>
            <Button
              className="me-2"
              color="secondary"
              outline
              onClick={() => {
                setNewGroupName("");
                setAddNewGroupOpen(false);
              }}
            >
              <FormattedMessage id="Cancel" />
            </Button>

            <Button
              color="primary"
              outline
              onClick={() => handlePostNewGroup()}
            >
              <FormattedMessage id="SaveNewGroup" />
            </Button>
          </Col>
        </>
      );
    } else {
      return (
        <Col>
          <Button
            color="primary"
            outline
            onClick={() => setAddNewGroupOpen(true)}
          >
            <FormattedMessage id="AddGroup" />
          </Button>
        </Col>
      );
    }
  };

  return (
    <>
      <Modal size="xl" isOpen={isOpen} toggle={close}>
        <ModalHeader toggle={close}>
          <FormattedMessage id="Add" />
        </ModalHeader>
        <ModalBody>
          <Row>{addNewGroup()}</Row>
        </ModalBody>
        {groups.data?.map((group) => {
          return (
            <GroupCard
              key={group.id}
              group={group}
              setNewChanges={setNewChanges}
              newChanges={newChanges}
              currentSchools={currentSchools}
              setCurrentSchools={setCurrentSchools}
              allSchools={schools}
              changedNames={changedNames}
              setChangedNames={setChangedNames}
              setPromptForDelete={setPromptForDelete}
            />
          );
        })}
        <ModalFooter>
          <Button onClick={() => handleCloseModal()} outline color="secondary">
            <FormattedMessage id="Cancel" />
          </Button>
          <Button onClick={() => onProceed()} outline color="primary">
            <FormattedMessage id="Save" />
          </Button>
        </ModalFooter>
      </Modal>
      <ConfirmDialog
        isOpen={promptForDelete !== null}
        toggle={() => setPromptForDelete(null)}
        title={<FormattedMessage id="AreYouSure" />}
        message={
          "Är du säker på att du vill ta bort " + promptForDelete?.name + "?"
        }
        onProceed={handleDeleteGroup}
        danger={true}
      />
    </>
  );
}
